import React, { useEffect, useState } from 'react';
import { FaEllipsisV, FaPenAlt, FaHistory, FaBoxOpen, FaTrash } from 'react-icons/fa';
import { formatMoney } from '../owners/formatMoney';
import { isLowStock, isActiveStatus, formatQty } from './partsUi';

const Badge = ({ className, children }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${className}`}>
        {children}
    </span>
);

const PartBadges = ({ part }) => (
    <>
        {part.has_serial && <Badge className="bg-orange-100 text-orange-700">SERIAL</Badge>}
        {part.has_serial && part.serials_pending && (
            <Badge className="bg-red-100 text-red-700">SERIALS MISSING</Badge>
        )}
        {part.is_stale_pricing && <Badge className="bg-red-100 text-red-700">Stale pricing</Badge>}
    </>
);

const StatusBadge = ({ status }) => (
    <Badge className={isActiveStatus(status) ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}>
        {status || 'UNKNOWN'}
    </Badge>
);

const StockValue = ({ part }) => {
    const low = isLowStock(part);
    const qty = Number(part.quantity || 0);
    const color = qty <= 0 ? 'text-red-600' : low ? 'text-amber-600' : 'text-gray-900';
    return <span className={`font-semibold ${color}`}>{formatQty(part.quantity)}</span>;
};

const MinMax = ({ part }) => {
    const min = Number(part.min_quantity || 0);
    const max = Number(part.max_quantity || 0);
    if (!min && !max) return <span className="text-gray-400">No limits set</span>;
    return <span>min {formatQty(min)} · max {formatQty(max)}</span>;
};

const actionsFor = (part, handlers) => [
    { label: 'Edit', Icon: FaPenAlt, onClick: () => handlers.onEdit(part) },
    { label: 'Price history', Icon: FaHistory, onClick: () => handlers.onPriceHistory(part) },
    { label: 'Stock card', Icon: FaBoxOpen, onClick: () => handlers.onStockCard(part) },
    { label: 'Delete', Icon: FaTrash, onClick: () => handlers.onDelete(part), danger: true }
];

/**
 * Parts as a table on desktop and as cards on phones.
 */
export default function PartsList({ parts, onEdit, onPriceHistory, onStockCard, onDelete }) {
    // Fixed-position menu so it is not clipped by the table's scroll container
    const [menu, setMenu] = useState(null);
    const handlers = { onEdit, onPriceHistory, onStockCard, onDelete };

    useEffect(() => {
        if (!menu) return undefined;
        const close = () => setMenu(null);
        const onKey = (e) => e.key === 'Escape' && close();
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
            window.removeEventListener('keydown', onKey);
        };
    }, [menu]);

    const openMenu = (e, part) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const menuHeight = 176;
        const top = rect.bottom + menuHeight > window.innerHeight ? rect.top - menuHeight - 4 : rect.bottom + 4;
        setMenu({ part, top, right: window.innerWidth - rect.right });
    };

    const menuPart = menu?.part;

    return (
        <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                        <tr>
                            <th className="px-4 py-3 text-left font-semibold">Part</th>
                            <th className="px-4 py-3 text-left font-semibold">Manufacturer / Category</th>
                            <th className="px-4 py-3 text-right font-semibold">Cost</th>
                            <th className="px-4 py-3 text-right font-semibold">Price</th>
                            <th className="px-4 py-3 text-right font-semibold">Stock</th>
                            <th className="px-4 py-3 text-center font-semibold">Status</th>
                            <th className="px-2 py-3"><span className="sr-only">Actions</span></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {parts.map((part) => (
                            <tr key={part.part_id} className="hover:bg-orange-50/40 align-top">
                                <td className="px-4 py-3 max-w-xs">
                                    <button
                                        type="button"
                                        onClick={() => onEdit(part)}
                                        className="text-left font-semibold text-[#2a2e30] hover:text-[#FF5733]"
                                    >
                                        {part.part_name}
                                    </button>
                                    <div className="text-xs text-gray-500 font-mono mt-0.5">
                                        {part.part_number} · {part.part_id}
                                    </div>
                                    {(part.has_serial || part.is_stale_pricing) && (
                                        <div className="flex flex-wrap gap-1 mt-1.5"><PartBadges part={part} /></div>
                                    )}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="text-gray-800">{part.manufacturer || '-'}</div>
                                    <div className="text-xs text-gray-500">{part.category || '-'}</div>
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <div className="text-gray-800">{formatMoney(part.cost_incl)}</div>
                                    <div className="text-xs text-gray-500">{formatMoney(part.cost_excl)} ex VAT</div>
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <div className="font-semibold text-gray-900">{formatMoney(part.price_incl)}</div>
                                    <div className="text-xs text-gray-500">{formatMoney(part.price_excl)} ex VAT</div>
                                    <div className="text-xs text-gray-500">{Number(part.margin_percent || 0).toFixed(1)}% margin</div>
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <StockValue part={part} />
                                    <div className="text-xs text-gray-500"><MinMax part={part} /></div>
                                </td>
                                <td className="px-4 py-3 text-center"><StatusBadge status={part.status} /></td>
                                <td className="px-2 py-3 text-right">
                                    <button
                                        type="button"
                                        onClick={(e) => openMenu(e, part)}
                                        className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                                        aria-label={`Actions for ${part.part_name}`}
                                        aria-haspopup="menu"
                                    >
                                        <FaEllipsisV />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Phone cards */}
            <div className="md:hidden space-y-3">
                {parts.map((part) => (
                    <div key={part.part_id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="font-semibold text-[#2a2e30] break-words">{part.part_name}</p>
                                <p className="text-xs text-gray-500 font-mono break-all">{part.part_number} · {part.part_id}</p>
                            </div>
                            <StatusBadge status={part.status} />
                        </div>
                        {(part.has_serial || part.is_stale_pricing) && (
                            <div className="flex flex-wrap gap-1 mt-2"><PartBadges part={part} /></div>
                        )}
                        <p className="text-xs text-gray-600 mt-2">
                            {[part.manufacturer, part.category].filter(Boolean).join(' · ') || '-'}
                        </p>
                        <dl className="grid grid-cols-3 gap-2 mt-3 text-sm">
                            <div>
                                <dt className="text-xs text-gray-500">Price</dt>
                                <dd className="font-semibold text-gray-900 break-words">{formatMoney(part.price_incl)}</dd>
                            </div>
                            <div>
                                <dt className="text-xs text-gray-500">Cost</dt>
                                <dd className="text-gray-800 break-words">{formatMoney(part.cost_incl)}</dd>
                            </div>
                            <div className="text-right">
                                <dt className="text-xs text-gray-500">Stock</dt>
                                <dd><StockValue part={part} /></dd>
                            </div>
                        </dl>
                        <p className="text-xs text-gray-500 text-right mt-1"><MinMax part={part} /></p>
                        <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-gray-100">
                            {actionsFor(part, handlers).map(({ label, Icon, onClick, danger }) => (
                                <button
                                    key={label}
                                    type="button"
                                    onClick={onClick}
                                    className={`flex flex-col items-center gap-1 py-2 rounded-lg text-[11px] font-medium ${danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-100'}`}
                                >
                                    <Icon size={14} />
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {menu && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setMenu(null)} />
                    <div
                        role="menu"
                        className="fixed z-50 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-xl"
                        style={{ top: menu.top, right: menu.right }}
                    >
                        {actionsFor(menuPart, handlers).map(({ label, Icon, onClick, danger }) => (
                            <button
                                key={label}
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                    setMenu(null);
                                    onClick();
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left ${danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`}
                            >
                                <Icon size={13} /> {label}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </>
    );
}
